import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Grid,
  IconButton,
  Paper,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PayloadType } from "../../types";
import { EditIcon } from "../Common/icons";
import { cellText, disabledSx, mobileCardSx } from "../Common/styles";
import FieldLabel from "../Common/FieldLabel";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

type PayloadParametersTableProps = {
  payloadData: PayloadType[];
  loading: boolean;
  showDeviceId?: boolean;
  onRowUpdate: (key: string, updates: Partial<PayloadType>) => void;
  onIsDeviceIdChange: (key: string, value: boolean) => void;
  onEditClick: (row: PayloadType) => void;
  onArrayExpandRowUpdate: (normalizedKey: string, updates: Partial<PayloadType>) => void;
};

const FilledSquareIcon = ({ color }: { color: string }) => (
  <SvgIcon viewBox="0 0 24 24">
    <rect x="3" y="3" width="18" height="18" rx="2" fill={color} />
  </SvgIcon>
);

const squareCheckboxSx = {
  p: "4px",
  "& .MuiSvgIcon-root": { fontSize: "20px" },
};

const headerCellSx = {
  fontWeight: 600,
  fontSize: 13,
  py: 1,
  backgroundColor: "#f8f9fa",
  borderBottom: "2px solid rgba(0,0,0,0.08)",
  color: "#333",
  whiteSpace: "nowrap" as const,
};

const descriptionFieldSx = {
  "& .MuiInputBase-input": { fontSize: 12, py: "4px" },
  "& .MuiOutlinedInput-root.Mui-disabled": { backgroundColor: "#f0f0f0" },
  "& .MuiOutlinedInput-input.Mui-disabled": { WebkitTextFillColor: "#333" },
};

const normalizeKey = (key: string) => key.replace(/\[\d+\]/g, "[*]");
const isArrayKey = (key: string) => /\[\d+\]/.test(key);

const KeyWithWildcard = ({ keyStr }: { keyStr: string }) => {
  const parts = keyStr.split(/(\[\*\])/);
  return (
    <Typography component="span" sx={cellText}>
      {parts.map((part, i) =>
        part === "[*]" ? (
          <Box component="span" key={i} sx={{ color: "var(--color-primary)", fontWeight: 700 }}>
            [*]
          </Box>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </Typography>
  );
};

type ArrayGroupItem = {
  type: "array-group";
  normalizedKey: string;
  children: PayloadType[];
  expandRow?: PayloadType;
};

type RegularItem = {
  type: "regular";
  row: PayloadType;
};

type DisplayItem = RegularItem | ArrayGroupItem;

const buildDisplayItems = (payloadData: PayloadType[]): DisplayItem[] => {
  const arrayExpandRows = payloadData.filter(x => x.isArrayExpand);
  const regularRows = payloadData.filter(x => !x.isArrayExpand);

  const items: DisplayItem[] = [];
  const processedKeys = new Set<string>();

  for (const row of regularRows) {
    if (!isArrayKey(row.key)) {
      items.push({ type: "regular", row });
    } else {
      const nk = normalizeKey(row.key);
      if (!processedKeys.has(nk)) {
        processedKeys.add(nk);
        items.push({
          type: "array-group",
          normalizedKey: nk,
          children: regularRows.filter(r => isArrayKey(r.key) && normalizeKey(r.key) === nk),
          expandRow: arrayExpandRows.find(r => r.key === nk),
        });
      }
    }
  }

  return items;
};

const PayloadParametersTable: React.FC<PayloadParametersTableProps> = ({
  payloadData,
  loading,
  showDeviceId,
  onRowUpdate,
  onIsDeviceIdChange,
  onEditClick,
  onArrayExpandRowUpdate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (normalizedKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(normalizedKey)) next.delete(normalizedKey);
      else next.add(normalizedKey);
      return next;
    });
  };

  const displayItems = buildDisplayItems(payloadData);

  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {displayItems.map(item => {
          if (item.type === "regular") {
            const row = item.row;
            return (
              <Box key={row.key} sx={mobileCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ ...cellText, fontWeight: 600, fontSize: 13 }}>{row.key}</Typography>
                    <Typography sx={{ ...cellText, color: "text.secondary" }}>{String(row.value ?? "")}</Typography>
                  </Box>
                  <IconButton size="small" disabled={!row.isRawData} onClick={() => onEditClick(row)}
                    sx={{ opacity: row.isRawData ? 1 : 0.3, flexShrink: 0 }}>
                    <EditIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={1} alignItems="center">
                  <Grid size={showDeviceId ? 4 : 6}>
                    <FieldLabel>Kaydet</FieldLabel>
                    <Checkbox
                      checked={row.dbRecord}
                      icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
                      checkedIcon={<FilledSquareIcon color="var(--color-save)" />}
                      sx={squareCheckboxSx}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onRowUpdate(row.key, { dbRecord: checked, isRawData: !checked && row.isRawData ? false : row.isRawData });
                      }}
                    />
                  </Grid>
                  <Grid size={showDeviceId ? 4 : 6}>
                    <FieldLabel>Parse Et</FieldLabel>
                    <Checkbox
                      checked={row.isRawData}
                      icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
                      checkedIcon={<FilledSquareIcon color="var(--color-primary)" />}
                      sx={squareCheckboxSx}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onRowUpdate(row.key, { isRawData: checked, dbRecord: checked ? true : row.dbRecord });
                      }}
                    />
                  </Grid>
                  {showDeviceId && (
                    <Grid size={4}>
                      <FieldLabel>Device Id</FieldLabel>
                      <Checkbox
                        checked={row.isDeviceId ?? false}
                        icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
                        checkedIcon={<FilledSquareIcon color="#0288d1" />}
                        sx={squareCheckboxSx}
                        onChange={(e) => onIsDeviceIdChange(row.key, e.target.checked)}
                      />
                    </Grid>
                  )}
                  <Grid size={12}>
                    <FieldLabel>Açıklama</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      value={row.description}
                      slotProps={{ htmlInput: { maxLength: 150 } }}
                      onChange={(e) => onRowUpdate(row.key, { description: e.target.value })}
                      disabled={!row.dbRecord || row.isRawData}
                      sx={{ "& .MuiInputBase-input": { fontSize: 12 }, ...disabledSx }}
                    />
                  </Grid>
                </Grid>
              </Box>
            );
          }

          const { normalizedKey, children, expandRow } = item;
          const isExpanded = expandedGroups.has(normalizedKey);
          const isActive = !!(expandRow?.dbRecord || expandRow?.isRawData);

          return (
            <Box key={normalizedKey}>
              <Box sx={{
                ...mobileCardSx,
                backgroundColor: isActive ? "rgba(255,152,0,0.07)" : "rgba(0,0,0,0.02)",
                border: `1px solid ${isActive ? "rgba(255,152,0,0.4)" : "rgba(0,0,0,0.08)"}`,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flex: 1 }}>
                    <IconButton size="small" onClick={() => toggleGroup(normalizedKey)} sx={{ p: 0.25 }}>
                      <ExpandMoreIcon sx={{ fontSize: 18, color: "var(--color-primary)", transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.2s" }} />
                    </IconButton>
                    <Tooltip title="Tüm array elemanlarına uygulanır." placement="top" arrow>
                      <span><KeyWithWildcard keyStr={normalizedKey} /></span>
                    </Tooltip>
                  </Box>
                </Box>
                <Grid container spacing={1} alignItems="center">
                  <Grid size={6}>
                    <FieldLabel>Kaydet</FieldLabel>
                    <Checkbox
                      checked={expandRow?.dbRecord ?? false}
                      icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
                      checkedIcon={<FilledSquareIcon color="var(--color-save)" />}
                      sx={squareCheckboxSx}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onArrayExpandRowUpdate(normalizedKey, { dbRecord: checked, isRawData: !checked ? false : expandRow?.isRawData ?? false });
                      }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <FieldLabel>Parse Et</FieldLabel>
                    <Checkbox
                      checked={expandRow?.isRawData ?? false}
                      icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
                      checkedIcon={<FilledSquareIcon color="var(--color-primary)" />}
                      sx={squareCheckboxSx}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onArrayExpandRowUpdate(normalizedKey, { isRawData: checked, dbRecord: checked ? true : expandRow?.dbRecord ?? false });
                      }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <FieldLabel>Açıklama</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      value={expandRow?.description ?? ""}
                      slotProps={{ htmlInput: { maxLength: 150 } }}
                      onChange={(e) => onArrayExpandRowUpdate(normalizedKey, { description: e.target.value })}
                      disabled={!expandRow?.dbRecord || expandRow?.isRawData}
                      sx={{ "& .MuiInputBase-input": { fontSize: 12 }, ...disabledSx }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {isExpanded && (
                <Box sx={{ pl: 2, mt: 0.5, display: "flex", flexDirection: "column", gap: 1, opacity: isActive ? 0.35 : 1, pointerEvents: isActive ? "none" : "auto" }}>
                  {children.map(child => (
                    <Box key={child.key} sx={mobileCardSx}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ ...cellText, fontWeight: 600, fontSize: 13 }}>{child.key}</Typography>
                          <Typography sx={{ ...cellText, color: "text.secondary" }}>{String(child.value ?? "")}</Typography>
                        </Box>
                        <IconButton size="small" disabled={!child.isRawData} onClick={() => onEditClick(child)}
                          sx={{ opacity: child.isRawData ? 1 : 0.3, flexShrink: 0 }}>
                          <EditIcon />
                        </IconButton>
                      </Box>
                      <Grid container spacing={1} alignItems="center">
                        <Grid size={6}>
                          <FieldLabel>Kaydet</FieldLabel>
                          <Checkbox
                            checked={child.dbRecord}
                            icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
                            checkedIcon={<FilledSquareIcon color="var(--color-save)" />}
                            sx={squareCheckboxSx}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              onRowUpdate(child.key, { dbRecord: checked, isRawData: !checked && child.isRawData ? false : child.isRawData });
                            }}
                          />
                        </Grid>
                        <Grid size={6}>
                          <FieldLabel>Parse Et</FieldLabel>
                          <Checkbox
                            checked={child.isRawData}
                            icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
                            checkedIcon={<FilledSquareIcon color="var(--color-primary)" />}
                            sx={squareCheckboxSx}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              onRowUpdate(child.key, { isRawData: checked, dbRecord: checked ? true : child.dbRecord });
                            }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <FieldLabel>Açıklama</FieldLabel>
                          <TextField
                            fullWidth size="small"
                            value={child.description}
                            slotProps={{ htmlInput: { maxLength: 150 } }}
                            onChange={(e) => onRowUpdate(child.key, { description: e.target.value })}
                            disabled={!child.dbRecord || child.isRawData}
                            sx={{ "& .MuiInputBase-input": { fontSize: 12 }, ...disabledSx }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  const renderRegularRow = (row: PayloadType, extraSx?: object) => (
    <TableRow key={row.key} sx={extraSx}>
      <TableCell sx={{ py: 0.5 }}>
        <Tooltip title={row.key} placement="top" arrow>
          <Typography sx={cellText}>{row.key}</Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ py: 0.5 }}>
        <Tooltip title={String(row.value ?? "")} placement="top" arrow>
          <Typography sx={cellText}>{String(row.value ?? "")}</Typography>
        </Tooltip>
      </TableCell>
      <TableCell align="center" sx={{ py: 0.5 }}>
        <Checkbox
          checked={row.dbRecord}
          icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
          checkedIcon={<FilledSquareIcon color="var(--color-save)" />}
          sx={squareCheckboxSx}
          onChange={(e) => {
            const checked = e.target.checked;
            onRowUpdate(row.key, { dbRecord: checked, isRawData: !checked && row.isRawData ? false : row.isRawData });
          }}
        />
      </TableCell>
      <TableCell align="center" sx={{ py: 0.5 }}>
        <Checkbox
          checked={row.isRawData}
          icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
          checkedIcon={<FilledSquareIcon color="var(--color-primary)" />}
          sx={squareCheckboxSx}
          onChange={(e) => {
            const checked = e.target.checked;
            onRowUpdate(row.key, { isRawData: checked, dbRecord: checked ? true : row.dbRecord });
          }}
        />
      </TableCell>
      {showDeviceId && (
        <TableCell align="center" sx={{ py: 0.5 }}>
          <Checkbox
            checked={row.isDeviceId ?? false}
            icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
            checkedIcon={<FilledSquareIcon color="#0288d1" />}
            sx={squareCheckboxSx}
            onChange={(e) => onIsDeviceIdChange(row.key, e.target.checked)}
          />
        </TableCell>
      )}
      <TableCell sx={{ py: 0.5 }}>
        <TextField
          fullWidth size="small"
          value={row.description}
          slotProps={{ htmlInput: { maxLength: 150 } }}
          onChange={(e) => onRowUpdate(row.key, { description: e.target.value })}
          disabled={!row.dbRecord || row.isRawData}
          sx={descriptionFieldSx}
        />
      </TableCell>
      <TableCell align="center" sx={{ py: 0.5 }}>
        <Tooltip title="Düzenle" placement="top" arrow>
          <span>
            <IconButton size="small" disabled={!row.isRawData} onClick={() => onEditClick(row)}
              sx={{ opacity: row.isRawData ? 1 : 0.3 }}>
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <Paper sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: "32%" }}>Parametre Adı</TableCell>
              <TableCell sx={{ ...headerCellSx, width: "18%" }}>Değer</TableCell>
              <TableCell sx={{ ...headerCellSx, width: "8%", textAlign: "center" }}>Kaydet</TableCell>
              <TableCell sx={{ ...headerCellSx, width: "8%", textAlign: "center" }}>Parse Et</TableCell>
              {showDeviceId && <TableCell sx={{ ...headerCellSx, width: "8%", textAlign: "center" }}>Device Id</TableCell>}
              <TableCell sx={{ ...headerCellSx, width: "16%" }}>Açıklama</TableCell>
              <TableCell sx={{ ...headerCellSx, width: "6%" }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {displayItems.map(item => {
              if (item.type === "regular") {
                return renderRegularRow(item.row);
              }

              const { normalizedKey, children, expandRow } = item;
              const isExpanded = expandedGroups.has(normalizedKey);
              const isActive = !!(expandRow?.dbRecord || expandRow?.isRawData);

              return (
                <React.Fragment key={normalizedKey}>
                  <TableRow sx={{ backgroundColor: isActive ? "rgba(255,152,0,0.08)" : "rgba(0,0,0,0.025)" }}>
                    <TableCell sx={{ py: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                        <IconButton size="small" onClick={() => toggleGroup(normalizedKey)} sx={{ p: 0.25, flexShrink: 0 }}>
                          <ExpandMoreIcon sx={{
                            fontSize: 18,
                            color: "var(--color-primary)",
                            transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                            transition: "transform 0.2s",
                          }} />
                        </IconButton>
                        <Tooltip
                          title="Tüm array elemanlarına uygulanır."
                          placement="top"
                          arrow
                        >
                          <span style={{ overflow: "hidden", minWidth: 0 }}><KeyWithWildcard keyStr={normalizedKey} /></span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      {(() => {
                        const firstVal = String(children.find(c => c.value !== null && c.value !== undefined)?.value ?? "");
                        return (
                          <Tooltip title={firstVal} placement="top" arrow>
                            <Typography sx={{ ...cellText, color: "text.secondary" }}>{firstVal}</Typography>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Checkbox
                        checked={expandRow?.dbRecord ?? false}
                        icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
                        checkedIcon={<FilledSquareIcon color="var(--color-save)" />}
                        sx={squareCheckboxSx}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          onArrayExpandRowUpdate(normalizedKey, { dbRecord: checked, isRawData: !checked ? false : expandRow?.isRawData ?? false });
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Checkbox
                        checked={expandRow?.isRawData ?? false}
                        icon={<CheckBoxOutlineBlankIcon sx={{ color: "#ccc" }} />}
                        checkedIcon={<FilledSquareIcon color="var(--color-primary)" />}
                        sx={squareCheckboxSx}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          onArrayExpandRowUpdate(normalizedKey, { isRawData: checked, dbRecord: checked ? true : expandRow?.dbRecord ?? false });
                        }}
                      />
                    </TableCell>
                    {showDeviceId && <TableCell sx={{ py: 0.5 }} />}
                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        fullWidth size="small"
                        value={expandRow?.description ?? ""}
                        slotProps={{ htmlInput: { maxLength: 150 } }}
                        onChange={(e) => onArrayExpandRowUpdate(normalizedKey, { description: e.target.value })}
                        disabled={!expandRow?.dbRecord || expandRow?.isRawData}
                        sx={descriptionFieldSx}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Tooltip title="Düzenle" placement="top" arrow>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!expandRow?.isRawData}
                            onClick={() => {
                              if (!expandRow) return;
                              const firstChild = children.find(c => c.value !== null && c.value !== undefined);
                              onEditClick({ ...expandRow, value: firstChild?.value ?? expandRow.value });
                            }}
                            sx={{ opacity: expandRow?.isRawData ? 1 : 0.3 }}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {isExpanded && children.map(child =>
                    renderRegularRow(child, {
                      opacity: isActive ? 0.35 : 1,
                      pointerEvents: isActive ? "none" : "auto",
                      "& td:first-of-type": { pl: 4 },
                    })
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PayloadParametersTable;
